/**
 * Get Your Sample Web Component
 * Finds matching sample products and adds them to cart.
 */
if (!customElements.get("get-sample")) {
  class GetSample extends HTMLElement {
    static MAX_SAMPLES = 4;
    static CACHE_TTL = 1000 * 60 * 60; // 1 hour

    connectedCallback() {
      this.btn = this.querySelector(".get-sample__btn");
      this.statusEl = this.querySelector(".get-sample__status");
      this.productTitle = this.dataset.productTitle;
      this.noSamples = this.dataset.noSamples === "true";
      this.noSampleRange = this.dataset.noSampleRange === "true";
      this.currentColour = this.dataset.currentColour || "";
      this.debugEnabled = this.isDebugEnabled();

      this.debugLog("initialized", {
        productTitle: this.productTitle,
        currentColour: this.currentColour,
        noSamples: this.noSamples,
        noSampleRange: this.noSampleRange,
      });

      if (this.noSamples || this.noSampleRange) {
        this.btn.disabled = true;
        this.showStatus(
          "info",
          this.noSamples
            ? "Samples are currently unavailable for this product."
            : "Samples are not available for this range.",
        );
        return;
      }

      this.btn.addEventListener("click", this.handleClick.bind(this));

      document.addEventListener(
        "on:variant:change",
        this.handleVariantChange.bind(this),
      );

      if (this.currentColour) {
        this.lookupAndUpdateButton();
      }
    }

    handleVariantChange(e) {
      const variant = e.detail && e.detail.variant;
      if (!variant) return;

      this.debugLog("variant changed", variant);

      const options = variant.options || [];
      if (options.length > 0) {
        this.currentColour = options[0];
        this.dataset.currentColour = this.currentColour;
      }

      this.updateButtonLabel();
      this.clearStatus();
      if (options.length > 0) {
        this.lookupAndUpdateButton();
      }
    }

    async lookupAndUpdateButton() {
      if (!this._lookupSeq) this._lookupSeq = 0;
      const seq = ++this._lookupSeq;
      this.btn.disabled = true;
      this.debugLog("checking availability", {
        productTitle: this.productTitle,
        currentColour: this.currentColour,
      });

      const variantId = await this.findSampleProduct();
      if (seq !== this._lookupSeq) return;

      this.debugLog("availability result", { variantId });
      if (variantId) {
        this._sampleUnavailable = false;
        this.updateButtonLabel();
        this.btn.disabled = false;
      } else {
        this._sampleUnavailable = true;
        this.btn.textContent = "Sample Unavailable";
        this.btn.disabled = true;
      }
    }

    async handleClick(e) {
      e.preventDefault();

      if (this.btn.disabled) return;

      this.debugLog("add sample clicked");

      const cartOk = await this.checkCartLimits();
      if (!cartOk) return;

      this.setLoading(true);

      try {
        const sampleVariantId = await this.findSampleProduct();
        this.debugLog("resolved sample variant for add", { sampleVariantId });

        if (!sampleVariantId) {
          this.showStatus(
            "error",
            "No matching sample found. Please contact us for assistance.",
          );
          this.setLoading(false);
          return;
        }

        const isDuplicate = await this.checkDuplicate(sampleVariantId);
        this.debugLog("duplicate check", { isDuplicate, sampleVariantId });

        if (isDuplicate) {
          this.showStatus("info", "This sample is already in your cart.");
          this.setLoading(false);
          return;
        }

        const response = await fetch(window.theme.routes.cartAdd, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [{ id: sampleVariantId, quantity: 1 }],
          }),
        });

        if (!response.ok) {
          this.debugLog("add-to-cart failed", { status: response.status });
          throw new Error("Failed to add sample to cart");
        }

        this.debugLog("sample added to cart", { sampleVariantId });
        this.showStatus("success", "Sample added to cart!");

        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
        setTimeout(function () {
          document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
        }, 300);
      } catch (err) {
        this.debugLog("sample add error", err);
        this.showStatus("error", "Could not add sample. Please try again.");
      }

      this.setLoading(false);
    }

    async checkCartLimits() {
      try {
        const response = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        const cart = await response.json();
        const sampleCount = cart.items.filter(function (item) {
          return (
            item.product_type === "Sample" ||
            (item.tags && item.tags.includes("SAMPLE"))
          );
        }).length;

        const sampleCountByTitle = cart.items.filter(function (item) {
          return item.title && item.title.toLowerCase().includes("sample");
        }).length;

        const totalSamples = Math.max(sampleCount, sampleCountByTitle);
        this.debugLog("sample count in cart", { totalSamples });

        if (totalSamples >= GetSample.MAX_SAMPLES) {
          this.showStatus(
            "info",
            "You have reached the maximum of " +
              GetSample.MAX_SAMPLES +
              " samples. Remove a sample from your cart to add a new one.",
          );
          return false;
        }

        return true;
      } catch (e) {
        this.debugLog("cart limits check error", e);
        return true;
      }
    }

    async checkDuplicate(variantId) {
      try {
        const response = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        const cart = await response.json();
        const isDuplicate = cart.items.some(function (item) {
          return item.variant_id === variantId;
        });
        this.debugLog("duplicate check response", { variantId, isDuplicate });
        return isDuplicate;
      } catch (e) {
        this.debugLog("duplicate check error", e);
        return false;
      }
    }

    async findSampleProduct() {
      const searchTerms = this.buildSearchTerms();
      const titleLower = (this.productTitle || "").toLowerCase();
      const colourLower = (this.currentColour || "").toLowerCase();

      for (var termIndex = 0; termIndex < searchTerms.length; termIndex++) {
        const searchTerm = searchTerms[termIndex];
        const cacheKey =
          "sample_" + searchTerm.replace(/\s+/g, "_").toLowerCase();

        this.debugLog("searching for sample product", { searchTerm, cacheKey });

        const cached = this.getCache(cacheKey);
        if (cached !== null) {
          if (cached !== false) {
            this.debugLog("sample lookup cache hit", { cacheKey, cached });
            return cached;
          }
          this.debugLog("sample lookup cache miss for term", { cacheKey, searchTerm });
        }

        const handleCandidates = this.buildHandleCandidates(searchTerm, titleLower, colourLower);
        for (var handleIndex = 0; handleIndex < handleCandidates.length; handleIndex++) {
          const handle = handleCandidates[handleIndex];
          const variantId = await this.findSampleProductByHandle(handle, searchTerm);
          if (variantId) {
            this.setCache(cacheKey, variantId);
            return variantId;
          }
        }

        this.debugLog("no matching sample product found", { searchTerm, handleCandidates });
        this.setCache(cacheKey, false);
      }

      return false;
    }

    async findSampleProductByHandle(handle, searchTerm) {
      if (!handle) return false;

      try {
        const url = "/products/" + encodeURIComponent(handle) + ".json";
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          this.debugLog("direct product lookup failed", { handle, status: response.status });
          return false;
        }

        const data = await response.json();
        const product = data.product;

        if (!product) {
          this.debugLog("direct product lookup returned no product", { handle });
          return false;
        }

        const productTitle = (product.title || "").toLowerCase();
        const tags = product.tags || [];
        const hasSampleTag = tags.includes("SAMPLE") || tags.includes("sample");
        const looksLikeSample =
          productTitle.includes("sample") ||
          hasSampleTag ||
          this.normalizeText(productTitle).includes("sample");

        this.debugLog("direct product lookup response", {
          handle,
          title: product.title,
          tags,
          looksLikeSample,
          variantCount: product.variants ? product.variants.length : 0,
        });

        if (!looksLikeSample || !product.variants || product.variants.length === 0) {
          return false;
        }

        const titleLower = (this.productTitle || "").toLowerCase();
        const colourLower = (this.currentColour || "").toLowerCase();
        const titleMatches =
          productTitle.includes(titleLower) ||
          productTitle.includes(colourLower) ||
          this.normalizeText(productTitle).includes(this.normalizeText(searchTerm));

        if (!titleMatches) {
          this.debugLog("direct product lookup title mismatch", {
            handle,
            productTitle: product.title,
            searchTerm,
          });
          return false;
        }

        const firstVariantId = product.variants[0].id;
        this.debugLog("matched sample product by direct handle", {
          handle,
          title: product.title,
          variantId: firstVariantId,
        });
        return firstVariantId;
      } catch (e) {
        this.debugLog("direct product lookup error", { handle, error: e });
        return false;
      }
    }

    buildHandleCandidates(searchTerm, titleLower, colourLower) {
      const candidates = [];
      const addCandidate = (value) => {
        const slug = this.slugify(value);
        if (slug && !candidates.includes(slug)) {
          candidates.push(slug);
        }
      };

      const baseTitle = (this.productTitle || "").trim();
      const baseColour = (this.currentColour || "").trim();
      const colourWithoutTitle = baseColour
        .replace(new RegExp("^" + this.escapeRegExp(baseTitle), "i"), "")
        .trim();

      addCandidate(`${baseTitle} ${colourWithoutTitle || baseColour} SAMPLE`);
      addCandidate(`${baseTitle} ${baseColour} SAMPLE`);
      addCandidate(`${baseColour} SAMPLE`);
      addCandidate(`${baseTitle} SAMPLE`);
      addCandidate(`${baseTitle} ${colourWithoutTitle || baseColour}`);
      addCandidate(`${baseTitle} ${baseColour}`);
      addCandidate(`${colourWithoutTitle || baseColour}`);
      addCandidate(searchTerm);

      if (titleLower && colourLower) {
        addCandidate(`${titleLower} ${colourLower} sample`);
        addCandidate(`${titleLower} ${colourLower}`);
        addCandidate(`${colourLower} sample`);
        addCandidate(`${titleLower} sample`);
      }

      return candidates;
    }

    buildSearchTerms() {
      const productTitle = (this.productTitle || "").trim();
      const colour = (this.currentColour || "").trim();
      const colourWithoutTitle = colour
        .replace(new RegExp("^" + this.escapeRegExp(productTitle), "i"), "")
        .trim();

      const candidates = [];
      const addCandidate = (value) => {
        const cleaned = (value || "").replace(/\s+/g, " ").trim();
        if (cleaned && !candidates.includes(cleaned)) {
          candidates.push(cleaned);
        }
      };

      addCandidate(`${productTitle} ${colourWithoutTitle || colour} SAMPLE`);
      addCandidate(`${productTitle} ${colour} SAMPLE`);
      addCandidate(`${colourWithoutTitle || colour} SAMPLE`);
      addCandidate(`${productTitle} SAMPLE`);
      addCandidate(`${colour} SAMPLE`);

      return candidates;
    }

    escapeRegExp(value) {
      return (value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    slugify(value) {
      return (value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .trim();
    }

    normalizeText(value) {
      return (value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    getCache(key) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - data.ts > GetSample.CACHE_TTL) {
          localStorage.removeItem(key);
          return null;
        }
        return data.value;
      } catch (e) {
        return null;
      }
    }

    setCache(key, value) {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ value: value, ts: Date.now() }),
        );
      } catch (e) {}
    }

    isDebugEnabled() {
      if (typeof window === "undefined") return false;

      try {
        const params = new URLSearchParams(window.location.search);
        if (
          params.get("get-sample-debug") === "1" ||
          params.get("get-sample-debug") === "true"
        ) {
          return true;
        }
      } catch (e) {}

      try {
        return window.localStorage.getItem("get-sample-debug") === "true";
      } catch (e) {
        return false;
      }
    }

    debugLog(message, payload) {
      if (!this.debugEnabled && !this.isDebugEnabled()) return;

      if (payload !== undefined) {
        console.debug("[get-sample]", message, payload);
      } else {
        console.debug("[get-sample]", message);
      }
    }

    updateButtonLabel() {
      const colour = (this.currentColour || "").trim();
      const label = colour ? `Get ${colour} Sample` : "Get Your Sample";
      this.btn.textContent = label;
      this.btn.dataset.originalText = label;
    }

    setLoading(loading) {
      if (loading) {
        this.btn.disabled = true;
        this.btn.dataset.originalText = this.btn.textContent;
        this.btn.innerHTML =
          '<span class="get-sample__spinner"></span>Finding sample...';
      } else {
        this.btn.textContent =
          this.btn.dataset.originalText || "Get Your Sample";
        this.btn.disabled = !!this._sampleUnavailable;
      }
    }

    showStatus(type, text) {
      this.statusEl.className =
        "get-sample__status get-sample__status--" + type;
      this.statusEl.textContent = text;
      this.statusEl.hidden = false;
    }

    clearStatus() {
      this.statusEl.hidden = true;
      this.statusEl.textContent = "";
    }
  }

  customElements.define("get-sample", GetSample);
}
